import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import * as React from 'react';
import { useIntl } from 'react-intl';

import IconButton from '@components/IconButton';

import { useSnackbar } from '@hooks/useSnackbar';

interface AudioAttachmentProps {
  src?: string;
  /** Callback that returns a new src */
  onBeforePlay?: () => Promise<string>;
}

interface PresignedAudioAttachmentProps {
  fetch: () => Promise<string>;
}

/**
 * Audio media player component
 */
function AudioAttachment({ onBeforePlay, src }: AudioAttachmentProps) {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const handlePlay = React.useCallback(async () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    // Step 1: Fetch the presigned URL
    const newSrc = await onBeforePlay?.();
    if (!newSrc) return;

    // Step 2: Pause any current audio, set the new source, and load
    audioEl.pause();
    audioEl.src = newSrc;
    audioEl.load();

    // Step 3: Wait for the audio to be ready
    await new Promise<void>((resolve) => {
      const onCanPlay = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        enqueueSnackbar({ message: 'Failed to load audio', variant: 'error' });
      };
      const cleanup = () => {
        audioEl.removeEventListener('canplay', onCanPlay);
        audioEl.removeEventListener('error', onError);
      };

      audioEl.addEventListener('canplay', onCanPlay);
      audioEl.addEventListener('error', onError);
    });

    // Step 4: Play
    await audioEl.play();
    setIsPlaying(true);
  }, [enqueueSnackbar, onBeforePlay]);

  const handlePause = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  const handleTimeUpdate: React.ReactEventHandler<HTMLAudioElement> = (
    event
  ) => {
    const target = event.target as HTMLAudioElement;
    const progress = (target.currentTime / target.duration) * 100;
    setProgress(progress);
    if (progress >= 100) {
      handlePause();
    }
  };

  const handleError = () => {
    enqueueSnackbar({
      message: formatMessage({ id: 'audio.playback.error' }),
      variant: 'error',
    });
  };

  return (
    <Box>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        style={{ display: 'none' }}
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onError={handleError}
      />
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          color={'teal' as any}
          variant="determinate"
          value={progress}
          size={28}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'info',
          }}
        >
          {isPlaying && (
            <IconButton
              onClick={handlePause}
              iconType="fas"
              color="teal.main"
              name="circle-pause"
              iconButtonProps={{
                size: 'small',
              }}
              size={18}
            />
          )}
          {!isPlaying && (
            <IconButton
              onClick={handlePlay}
              iconType="fas"
              color="teal.main"
              name="circle-play"
              iconButtonProps={{
                size: 'small',
              }}
              size={18}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Fetches a new presigned URL if the old one is expired before playing
 */
function withPresignedUrlFetch(
  Component: React.FunctionComponent<AudioAttachmentProps>
) {
  return ({ fetch }: PresignedAudioAttachmentProps) => {
    const [presignedUrl, setPresignedUrl] = React.useState<string>();

    const fetchNewUrl = async () => {
      const response = await fetch();
      return response;
    };

    const checkExpiry = async () => {
      let isExpired = false;
      let newUrl = presignedUrl;
      if (!presignedUrl) {
        // is expired
        isExpired = true;
      } else {
        //* See AWS S3 presigned URL documentation

        // Parse presigned URL search params
        const { searchParams } = new URL(presignedUrl);
        const date = searchParams.get('X-Amz-Date');
        const expires = searchParams.get('X-Amz-Expires') ?? '0';

        // Check if expired
        isExpired = dayjs(date)
          .add(parseInt(expires), 'seconds')
          .isAfter(dayjs());
      }

      if (isExpired || !newUrl) {
        // Fetch new presigned URL
        newUrl = await fetchNewUrl();
        setPresignedUrl(newUrl);
      }
      return newUrl;
    };

    return <Component onBeforePlay={checkExpiry} />;
  };
}

const PresignedAudioAttachment = withPresignedUrlFetch(AudioAttachment);

export default AudioAttachment;
export { PresignedAudioAttachment };
